import { useCallback, useMemo } from 'react';

import { Button, Icons } from '@commenda/kakashi';
import { Attachment, type AttachmentItem } from '@commenda/pathfinder';

import { DataRoomAttachmentHelper } from './DataRoomAttachmentHelper';
import { DataRoomUploadHelper } from './DataRoomUploadHelper';
import type { IDataRoomUploadProgress } from './types';

interface DataRoomUploadProgressProps {
  progress: IDataRoomUploadProgress | null;
  /** The room is being created or re-read, before any file row exists. */
  isPreparing: boolean;
  error: string | null;
  /** Set after a reload while an upload is unfinished: the files must be chosen again to resume it. */
  resumeFileCount: number | null;
  onFilesChoose: () => void;
  /** Null once the room has stopped on an error no retry can fix. */
  onFileRetry: ((fileId: string) => void) | null;
  /** Removes that one file; removing the last one cancels the upload. */
  onFileRemove: (fileId: string) => void;
  onUploadCancel: () => void;
}

/** The data room inside the message box, above the text: one Pathfinder Attachment pill per file. A pill
 * fills its ring while uploading, turns red when it failed (a click retries it) and shows its file icon
 * once stored. Its × removes that file; on the last file it cancels the upload. */
export const DataRoomUploadProgress = ({
  progress,
  isPreparing,
  error,
  resumeFileCount,
  onFilesChoose,
  onFileRetry,
  onFileRemove,
  onUploadCancel,
}: DataRoomUploadProgressProps) => {
  const attachmentItems = useMemo(
    () =>
      progress
        ? DataRoomUploadHelper.visibleFileRows(progress.files).map((row) =>
            DataRoomAttachmentHelper.toUploadingItem(row)
          )
        : [],
    [progress]
  );
  const hiddenFileCount = useMemo(
    () => (progress ? progress.files.length - attachmentItems.length : 0),
    [progress, attachmentItems]
  );
  const onAttachmentRetry = useMemo(
    () =>
      onFileRetry ? (item: AttachmentItem) => onFileRetry(item.id) : undefined,
    [onFileRetry]
  );
  const onAttachmentRemove = useCallback(
    (item: AttachmentItem) => onFileRemove(item.id),
    [onFileRemove]
  );

  return (
    <div className="flex w-full flex-col gap-md px-xs pt-md">
      {isPreparing && (
        <div className="flex items-center gap-md">
          <Icons.Loading01 className="size-4 shrink-0 animate-spin text-fg-secondary" />
          <p className="txt-small-medium flex-1 text-text-primary">
            Preparing the upload
          </p>
          <Button
            variant="tertiary"
            size="xs"
            type="button"
            onClick={onUploadCancel}
          >
            Cancel
          </Button>
        </div>
      )}
      {resumeFileCount !== null && !progress && (
        <div className="flex items-center gap-md">
          <Icons.UploadCloud01 className="size-4 shrink-0 text-fg-secondary" />
          <p className="txt-small-medium flex-1 text-text-primary">
            Finish uploading {resumeFileCount} files
          </p>
          <Button
            variant="secondary"
            size="xs"
            type="button"
            onClick={onFilesChoose}
          >
            Choose the files again
          </Button>
          <Button
            variant="tertiary"
            size="xs"
            type="button"
            onClick={onUploadCancel}
          >
            Cancel
          </Button>
        </div>
      )}
      {attachmentItems.length > 0 && (
        <div className="flex max-h-40 flex-wrap items-center gap-sm overflow-y-auto">
          {attachmentItems.map((item) => (
            <Attachment
              key={item.id}
              item={item}
              onRetry={onAttachmentRetry}
              onRemove={onAttachmentRemove}
            />
          ))}
          {hiddenFileCount > 0 && (
            <span className="txt-extra-small-regular text-text-tertiary">
              And {hiddenFileCount} more files
            </span>
          )}
        </div>
      )}
      {error && (
        <p className="txt-extra-small-regular text-text-error">{error}</p>
      )}
    </div>
  );
};
